import { UserInfo } from '@/types/auth';
import { FilterConfigDynamic } from '@/types/filter/filterConfig';
import { SelectItem } from '@/types/ui/selectItem';
import { CubeRawRow } from '@/utils/cube/mapper.type';

type CachedDynamicOptions = {
  rows: readonly CubeRawRow[];
  userInfo: UserInfo | undefined;
  options: SelectItem[];
};

const mappedOptionsCache = new WeakMap<FilterConfigDynamic, CachedDynamicOptions[]>();
const MAX_PERSISTENT_ROW_SETS = 2;

function getCachedOptions(config: FilterConfigDynamic, rows: readonly CubeRawRow[]): CachedDynamicOptions | undefined {
  return mappedOptionsCache.get(config)?.find(cachedOptions => cachedOptions.rows === rows);
}

function cacheMappedOptions(
  config: FilterConfigDynamic,
  rows: readonly CubeRawRow[],
  userInfo: UserInfo | undefined,
  options: SelectItem[],
): void {
  const cachedOptions = mappedOptionsCache.get(config) ?? [];
  const capacity = config.cacheMappedOptions ? MAX_PERSISTENT_ROW_SETS : 1;
  const updatedCache = [
    { rows, userInfo, options },
    ...cachedOptions.filter(cachedOption => cachedOption.rows !== rows),
  ].slice(0, capacity);
  mappedOptionsCache.set(config, updatedCache);
}

/**
 * Turn raw cube rows into the option list for a dynamic filter: map each row,
 * drop nulls and duplicates, then apply the filter's optional post-processing
 * and sort. Extracted from `useGetFilterOptions` so the transform is unit-
 * testable in isolation from React Query and the surrounding hook state.
 */
export const mapDynamicFilterOptions = (
  config: FilterConfigDynamic,
  rows: readonly CubeRawRow[],
  userInfo: UserInfo | undefined,
): SelectItem[] => {
  const cached = getCachedOptions(config, rows);
  if (cached?.rows === rows && cached.userInfo === userInfo) return cached.options;

  const seen = new Set<string>();
  const mapped: SelectItem<string>[] = [];
  for (const cubeRow of rows) {
    const option = config.mapResult(cubeRow, { userInfo });
    if (option === null || seen.has(option.value)) continue;
    seen.add(option.value);
    mapped.push(option);
  }

  const processed = config.processOptions ? config.processOptions(mapped) : mapped;
  const options = config.sort ? config.sort(processed) : processed;

  cacheMappedOptions(config, rows, userInfo, options);

  return options;
};
